<template>
  <SbFlexWrapped
    :depth="2"
    :threshold="32"
    :disabled="true"
    element="nav"
    data-sb-regression="sb-pagination"
    class="sb-pagination"
  >
    <template v-if="hasCurrentAndTotalPages">
      <div class="sb-pagination__pages">
        <!--
          @slot Slot to show the pagination links
        -->
        <slot name="pagination-links" />
      </div>
      <div class="sb-pagination__page-info">
        <!--
          @slot Slot to show the pagination text
        -->
        <slot
          name="pagination-text"
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-records="totalRecords"
          :visible-records="visibleRecords"
        />
        <!--
          @slot Slot to show the pagination input to switch pages
        -->
        <slot name="pagination-input" />
      </div>
    </template>
    <div v-else />
  </SbFlexWrapped>
</template>

<script>
import SbFlexWrapped from '@src/components/FlexWrapped/SbFlexWrapped.vue';
import cloneDeep from 'lodash/cloneDeep';
import paginationModel from '@src/components/Pagination/utils/sbPaginationModel';
/**
 * Event to handle fetching page.
 * The function arguments is `{ page, pagination }`
 *
 * @event fetchPage
 * @type Function
 */

/**
 * Navigation for grid data
 */
export default {
  name: 'SbPagination',
  components: { SbFlexWrapped },
  provide() {
    return {
      pagination: this.pagination,
      fetchPage: this.fetchPage,
    };
  },
  props: {
    /**
    * Number of records in a page
    */
    pageSize: {
      type: Number,
      required: false,
      default: paginationModel.pageSize,
    },
    /**
    * The current page number that is being displayed
    */
    currentPage: {
      type: Number,
      default: paginationModel.currentPage,
    },
    /**
    * Total number of pages in the data set
    */
    totalPages: {
      type: Number,
      required: false,
      default: paginationModel.totalPages,
    },
    /**
    * Total number of records in the data set
    */
    totalRecords: {
      type: Number,
      required: false,
      default: paginationModel.totalRecords,
    },
    /**
    * Total number of records being shown in the table. This will be less than or equal to the page size.
    */
    visibleRecords: {
      type: Number,
      required: false,
      default: paginationModel.visibleRecords,
    },
  },
  emits: [
    /**
    * Emitted with page and pagination info when page is changed via pagination.
    */
    'fetchPage',
  ],
  data: () => ({
    pagination: cloneDeep(paginationModel),
  }),
  computed: {
    hasCurrentAndTotalPages() {
      return this.currentPage !== null
              && this.totalPages !== null
              && this.currentPage > 0
              && this.totalPages > 0;
    },
  },
  watch: {
    pageSize: {
      immediate: true,
      handler(pageSize) {
        this.pagination.pageSize = pageSize;
      },
    },
    currentPage: {
      immediate: true,
      handler(currentPage) {
        if (this.pagination.currentPage !== currentPage) {
          this.pagination.currentPage = currentPage;
          this.fetchPage(currentPage);
        }
      },
    },
    totalPages: {
      immediate: true,
      handler(totalPages) {
        this.pagination.totalPages = totalPages;
      },
    },
    totalRecords: {
      immediate: true,
      handler(totalRecords) {
        this.pagination.totalRecords = totalRecords;
      },
    },
    visibleRecords: {
      immediate: true,
      handler(visibleRecords) {
        this.pagination.visibleRecords = visibleRecords;
      },
    },
  },
  methods: {
    fetchPage(page) {
      this.$emit('fetchPage', {
        page,
        pagination: this.pagination,
      });
    },
  },
};
</script>

<style lang="scss">
@import '@src/components/Pagination/style/sb-pagination.scss';
</style>
